import React, { useEffect, useState } from 'react';
import { auth, db } from '../../firebase/config';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, query, where, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import GiftCards from './GiftCards';
import { presentActivityLog } from '../utils/activityLogRendering';
import { isWithinSurveyWindow } from '../utils/surveyEligibility';
import { readAcquisitionStore, clearAcquisitionStore } from '../utils/acquisitionStore';
import '../styles/Dashboard.css';

// uids confirmed to have answered the acquisition survey, cached for the
// SPA session so section navigation does not re-query Firestore. A full
// page reload clears it, which just re-runs the query.
const answeredSurveyUids = new Set();

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLogs, setActivityLogs] = useState([]);
  const [selectedSection, setSelectedSection] = useState('overview');
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [qrSize, setQrSize] = useState(420);

  // Dynamically size QR code for anonymous sessions
  useEffect(() => {
    const computeQrSize = () => {
      const minViewport = Math.min(window.innerWidth, window.innerHeight);
      // occupy ~80% of the smaller viewport dimension, with sensible bounds
      const target = Math.round(minViewport * 0.8);
      const clamped = Math.max(240, Math.min(target, 900));
      setQrSize(clamped);
    };

    computeQrSize();
    window.addEventListener('resize', computeQrSize);
    return () => window.removeEventListener('resize', computeQrSize);
  }, []);

  useEffect(() => {
    // Check for section parameter in URL
    const urlParams = new URLSearchParams(location.search);
    const sectionParam = urlParams.get('section');
    
    if (sectionParam && ['overview', 'activity', 'stamps', 'giftcards'].includes(sectionParam)) {
      setSelectedSection(sectionParam);
    }
  }, [location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Belt and braces (see Auth.js): an email/password account whose
        // email is not verified must never use the dashboard. Auth.js signs
        // such users out at login, but a session persisted by an older build
        // could still land here directly.
        const hasPasswordProvider = user.providerData.some(
          (provider) => provider.providerId === 'password'
        );
        if (!user.isAnonymous && hasPasswordProvider && !user.emailVerified) {
          console.warn('Unverified email/password account - signing out');
          await signOut(auth);
          navigate('/auth');
          return;
        }
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userDataFromDB = userDoc.data();
            setUserData(userDataFromDB);
            // Anonymous users see only the new-user survey and their QR code
            // (the section nav/content is gated on !isAnonymous below).
            if (userDataFromDB.isAnonymous) {
              // Check if anonymous session has expired (24 hours)
              if (userDataFromDB.createdAt) {
                const sessionAge = Date.now() - userDataFromDB.createdAt.toDate().getTime();
                const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
                
                if (sessionAge > maxSessionAge) {
                  console.log('⏰ Anonymous session expired, signing out...');
                  await signOut(auth);
                  navigate('/auth');
                  return;
                }
              }
            }
            
            // Show the acquisition survey iff BOTH hold:
            //  1. The account is younger than 1 day (Lily 2026-09-01) -
            //     established members are never surprised with the form.
            //  2. This user has not answered it yet: no
            //     user_acquisition_sources doc for this uid with a non-null
            //     source. Bare doc existence is not enough - the intro
            //     pages pre-create a store-tracking doc with source: null
            //     before the user ever sees the survey.
            // Old accounts skip the Firestore check entirely.
            const inSurveyWindow = isWithinSurveyWindow(user.metadata?.creationTime);
            let hasAnsweredSurvey = answeredSurveyUids.has(user.uid);
            if (inSurveyWindow && !hasAnsweredSurvey) {
              try {
                const acquisitionSnapshot = await getDocs(query(
                  collection(db, 'user_acquisition_sources'),
                  where('userId', '==', user.uid)
                ));
                hasAnsweredSurvey = acquisitionSnapshot.docs.some(
                  (acqDoc) => acqDoc.data().source != null
                );
                if (hasAnsweredSurvey) {
                  answeredSurveyUids.add(user.uid);
                }
              } catch (surveyError) {
                // If the check fails, skip the survey rather than risk
                // re-asking someone who already answered; the next mount
                // retries.
                console.error('Error checking acquisition survey status:', surveyError.message);
                hasAnsweredSurvey = true;
              }
            }
            console.log(!inSurveyWindow
              ? '🚫 Account older than 1 day - acquisition survey not shown'
              : hasAnsweredSurvey
                ? '🚫 Acquisition survey already answered - not showing'
                : '🎯 No acquisition survey answer yet - showing survey');
            setShowSurvey(inSurveyWindow && !hasAnsweredSurvey);
            setSurveySubmitted(hasAnsweredSurvey);
            
            // Fetch activity logs only for non-anonymous users
            if (!userDataFromDB.isAnonymous) {
              const logsQuery = query(
                collection(db, 'activity_logs'),
                where('userID', '==', user.uid),
                orderBy('timestamp', 'desc')
              );
              const logsSnapshot = await getDocs(logsQuery);
              const logs = logsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate()
              }));
              setActivityLogs(logs);
            }
          } else if (user.isAnonymous) {
            // Self-heal: an authed anonymous user with no users doc is an
            // orphan (the intro page's doc write failed or the tab closed
            // between sign-in and write). Create the doc now so the QR/session
            // works instead of showing an empty dashboard.
            console.warn('🩹 Anonymous user has no profile doc - creating it now');
            await setDoc(doc(db, 'users', user.uid), {
              email: null,
              name: 'Temporary User',
              points: 0,
              coins: 0,
              stamps: 0,
              emailVerified: false,
              isAnonymous: true,
              lastSignInTime: serverTimestamp(),
              createdAt: serverTimestamp()
            });
            const healedDoc = await getDoc(doc(db, 'users', user.uid));
            if (healedDoc.exists()) {
              setUserData(healedDoc.data());
            }
          } else {
            console.error("No user data found");
          }
        } catch (error) {
          console.error("Error fetching user data:", error.message);
        }
      } else {
        navigate('/auth');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, location.search]);

  // Only reachable for signed-in (non-anonymous) users: the logout button is
  // not rendered for anonymous sessions.
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error("Error logging out:", error.message);
    }
  };

  const handleSurveySubmit = async (source) => {
    try {
      // Save survey response to user_acquisition_sources collection.
      // isAnonymous distinguishes guest (one-time QR) answers from member
      // answers - anonymous accounts get cleaned up quarterly (5.3), so
      // this flag is what keeps the funnel analytics interpretable after
      // their auth records are gone (Lily 2026-09-01).
      // store comes from the per-store poster URL the visitor arrived
      // through (WEB-Q11 Design B); null = organic/unknown, never guessed
      await addDoc(collection(db, 'user_acquisition_sources'), {
        userId: auth.currentUser.uid,
        source: source,
        store: readAcquisitionStore(),
        isAnonymous: auth.currentUser.isAnonymous === true,
        submittedAt: serverTimestamp()
      });
      clearAcquisitionStore();

      // Mark user as having completed the survey
      answeredSurveyUids.add(auth.currentUser.uid);
      setSurveySubmitted(true);
      setShowSurvey(false);
    } catch (error) {
      console.error("Error submitting survey:", error.message);
    }
  };

  const renderSurvey = () => (
    <div className="survey-overlay">
      <div className="survey-modal">
        <div className="survey-header">
          <div className="survey-icon">📢</div>
          <h2>How Did You Hear About Us?</h2>
          <p>We'd love to know how you discovered Lucky Claww!</p>
        </div>
        
        <div className="survey-options">
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('friends_referral')}
          >
            <span className="option-icon">👥</span>
            <span className="option-text">Friends Referral</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('google_maps')}
          >
            <span className="option-icon">🗺️</span>
            <span className="option-text">Google Maps</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('google_search')}
          >
            <span className="option-icon">🔍</span>
            <span className="option-text">Google Search</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('tiktok')}
          >
            <span className="option-icon">🎵</span>
            <span className="option-text">TikTok</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('instagram_facebook')}
          >
            <span className="option-icon">📷</span>
            <span className="option-text">Instagram / Facebook</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('walked_by_nearby_store')}
          >
            <span className="option-icon">🏬</span>
            <span className="option-text">Walked by / Visiting Nearby Store</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('yelp')}
          >
            <span className="option-icon">⭐</span>
            <span className="option-text">Yelp</span>
          </button>
          
          <button 
            className="survey-option" 
            onClick={() => handleSurveySubmit('others')}
          >
            <span className="option-icon">⋯</span>
            <span className="option-text">Others</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderOverviewSection = () => (
    <div className="overview-section">
      <div className="user-stats">
        <div className="stat-card">
          <h3>Points</h3>
          <p className="stat-value">{userData.points}</p>
        </div>
        {userData.coins > 0 && (
          <div className="stat-card">
            <h3>Tokens</h3>
            <p className="stat-value">{userData.coins}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderActivityLogsSection = () => {
    const presentedLogs = activityLogs
      .map((log) => ({ log, presentation: presentActivityLog(log) }))
      .filter(({ presentation }) => presentation !== null);

    return (
      <div className="activity-logs">
        {presentedLogs.map(({ log, presentation }) => (
          <div key={log.id} className="activity-log-item">
            <div className="log-header">
              <span className="log-timestamp">
                {log.timestamp?.toLocaleString()}
              </span>
              {presentation.label && (
                <span className="log-type">{presentation.label}</span>
              )}
            </div>
            <div className="log-changes">
              {presentation.changes.map((change) => (
                <div key={change.key} className={`${change.key}-change`}>
                  <span>{change.title}: </span>
                  <span className="previous-value">{change.previous}</span>
                  <span> → </span>
                  <span className="new-value">{change.next}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {presentedLogs.length === 0 && (
          <div className="no-logs">No activity history available</div>
        )}
      </div>
    );
  };

  const renderStampCollectionSection = () => (
    <div className="stamp-collection-section">
      <h3>Stamp Card</h3>
      <div className="stamps-info">
        <p>You have collected {userData.stamps || 0} stamps!</p>
        {userData.stamps >= 8 ? (
          <p className="stamps-complete">Congratulations! You've completed your stamp collection! 🎉</p>
        ) : (
          <p className="stamps-remaining">Collect {8 - (userData.stamps || 0)} more stamps to get a special prize!</p>
        )}
      </div>
      <div className="stamps-container">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="stamp-wrapper">
            <img src="/stamp.png" alt="Empty stamp" className="stamp-image" />
            {index < (userData.stamps || 0) && (
              <img src="/lc-logo.png" alt="Collected stamp" className="stamp-overlay" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="dashboard-container">
        <div className="error">Unable to load user data. Please try again.</div>
      </div>
    );
  }

  return (
    <div className={`dashboard-container ${userData.isAnonymous ? 'anonymous' : ''}`}>
      <div className={`dashboard-header ${userData.isAnonymous ? 'anonymous' : ''}`}>
        <div className="user-info">
          <h1 className="dashboard-title">
            {userData.isAnonymous ? (
              <>
                Quick Play Mode
                <span className="user-email">(Temporary Session)</span>
              </>
            ) : (
              <>
                <span className="welcome-word">Welcome,</span>
                <span className="welcome-identity">{userData.name || userData.email}</span>
                {userData.name && <span className="user-email">({userData.email})</span>}
              </>
            )}
          </h1>
        </div>
        
        {auth.currentUser && (
          <div className="header-qr-section">
            {!userData.isAnonymous && (
              <p className="qr-purchase-hint">
                Present your QR code to an attendant to purchase a token package
              </p>
            )}
            <div className="qr-code-container">
              <QRCodeSVG 
                value={auth.currentUser.uid}
                size={userData.isAnonymous ? qrSize : 180}
                level="H"
                includeMargin={false}
              />

            </div>
          </div>
        )}
      </div>

      {!userData.isAnonymous && (
        <div className="dashboard-content">
          <div className="section-navigation">
            <>
              <div 
                className={`nav-card ${selectedSection === 'overview' ? 'active' : ''}`}
                onClick={() => setSelectedSection('overview')}
              >
                <div className="nav-icon">📊</div>
                <div className="nav-info">
                  <div className="nav-title">Overview</div>
                  <div className="nav-details">
                    <div className="detail-item">
                      <span className="detail-label">Points:</span>
                      <span className="detail-value">{userData.points}</span>
                    </div>
                    {userData.coins > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Tokens:</span>
                        <span className="detail-value">{userData.coins}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div 
                className={`nav-card ${selectedSection === 'activity' ? 'active' : ''}`}
                onClick={() => setSelectedSection('activity')}
              >
                <div className="nav-icon">📝</div>
                <div className="nav-info">
                  <div className="nav-title">Activity History</div>
                  <div className="nav-details">
                    <div className="detail-item">
                      View your points and tokens history
                    </div>
                  </div>
                </div>
              </div>

              <div 
                className={`nav-card ${selectedSection === 'stamps' ? 'active' : ''}`}
                onClick={() => setSelectedSection('stamps')}
              >
                <div className="nav-icon">🧸</div>
                <div className="nav-info">
                  <div className="nav-title">Stamp Card</div>
                  <div className="nav-details">
                    <div className="detail-item">
                      <span className="detail-label">Stamps:</span>
                      <span className="detail-value">{userData.stamps || 0}/8</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`nav-card ${selectedSection === 'giftcards' ? 'active' : ''}`}
                onClick={() => setSelectedSection('giftcards')}
              >
                <div className="nav-icon">🎁</div>
                <div className="nav-info">
                  <div className="nav-title">Gift Cards</div>
                  <div className="nav-details">
                    <div className="detail-item">
                      Activate or manage gift cards
                    </div>
                  </div>
                </div>
              </div>
            </>
          </div>

          <div className="section-content">
            {selectedSection === 'overview' && renderOverviewSection()}
            {selectedSection === 'activity' && renderActivityLogsSection()}
            {selectedSection === 'stamps' && renderStampCollectionSection()}
            {selectedSection === 'giftcards' && <GiftCards userId={auth.currentUser.uid} />}
          </div>
        </div>
      )}

      {!userData.isAnonymous && (
        <button className="logout-button" onClick={handleLogout}>
          Log Out
        </button>
      )}
      {showSurvey && !surveySubmitted && renderSurvey()}
    </div>
  );
};

export default Dashboard;
